import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Form, Button, Row, Col, Spinner } from 'react-bootstrap';
import { FiSave, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import adminService, { CreateUserDto, Role } from '../../services/admin.service';

const UserForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);

  const [formData, setFormData] = useState<CreateUserDto & { is_active?: boolean }>({
    email: '',
    password: '',
    full_name: '',
    role_id: 3,
    phone: ''
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const rolesData = await adminService.listRoles();
      setRoles(rolesData);

      if (isEdit && id) {
        const user = await adminService.getUserById(parseInt(id));
        setFormData({
          email: user.email,
          password: '',
          full_name: user.full_name,
          role_id: user.role_id || 3,
          phone: user.phone || '',
          is_active: user.is_active
        });
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.full_name) {
      toast.error('Por favor complete los campos requeridos');
      return;
    }

    if (!isEdit && !formData.password) {
      toast.error('La contraseña es requerida para nuevos usuarios');
      return;
    }

    try {
      setSaving(true);

      const dataToSend: any = {
        email: formData.email,
        full_name: formData.full_name,
        role_id: Number(formData.role_id),
        phone: formData.phone || undefined
      };

      if (formData.password) {
        dataToSend.password = formData.password;
      }

      if (isEdit) {
        dataToSend.is_active = formData.is_active;
      }

      if (isEdit && id) {
        await adminService.updateUser(parseInt(id), dataToSend);
        toast.success('Usuario actualizado');
      } else {
        await adminService.createUser(dataToSend);
        toast.success('Usuario creado');
      }

      navigate('/users');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al guardar el usuario');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center mb-4">
        <Button variant="link" className="p-0 me-3" onClick={() => navigate('/users')}>
          <FiArrowLeft size={24} />
        </Button>
        <h1 className="h3 mb-0">{isEdit ? 'Editar Usuario' : 'Nuevo Usuario'}</h1>
      </div>

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Nombre Completo *</Form.Label>
                  <Form.Control
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder="Nombre completo"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="correo@ejemplo.com"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Rol *</Form.Label>
                  <Form.Select
                    name="role_id"
                    value={formData.role_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Seleccione un rol</option>
                    {roles.map(role => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Teléfono</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+57 300 123 4567"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Contraseña {!isEdit && '*'}</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={isEdit ? 'Dejar vacío para no cambiar' : 'Contraseña'}
                    required={!isEdit}
                  />
                  {isEdit && (
                    <Form.Text className="text-muted">
                      Dejar vacío para mantener la contraseña actual
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              {isEdit && (
                <Col md={6}>
                  <Form.Group className="mt-4">
                    <Form.Check
                      type="switch"
                      id="is_active"
                      name="is_active"
                      label="Usuario activo"
                      checked={formData.is_active || false}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>

            <div className="d-flex justify-content-end mt-4 gap-2">
              <Button variant="secondary" onClick={() => navigate('/users')}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <FiSave className="me-2" />
                    {isEdit ? 'Actualizar' : 'Crear'} Usuario
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default UserForm;
